<template>
  <v-form ref="formRef" v-model="valid">
    <v-row no-gutters>
      <InputField
        :rules="textFieldRules"
        label="Feld Name"
        v-model="attributeName"
        mandatory
      />
    </v-row>
    <v-row no-gutters>
      <InputField label="Beschreibung" v-model="attributeDescription" />
    </v-row>
    <v-row no-gutters>
      <CustomAttributeTypeSelection
        :attribute-type="attributeType"
        @update:attributeType="handleTypeSelection"
      />
    </v-row>
    <v-row no-gutters class="mb-1" v-if="isCurrentlySelectType">
      <ChipSelectCombo
        :mandatory="true"
        label="Optionen zur Auswahl"
        :value="selectionValues"
        @input="handleSelectTypeValuesChange($event)"
        :error="validationErrors.selectionValues"
        noDataHTMLText="Klicke auf <kbd>enter</kbd> um eine neue Option zu erstellen."
      />
    </v-row>
    <v-row no-gutters v-if="attributeTypeChanged">
      <v-alert width="100%" type="warning">
        <strong>Warnung:</strong> Wenn Sie den Feldtyp ändern, könnten sich
        bereits gespeicherte Werte ändern.
        <a
          style="color: white"
          href="https://suus.atlassian.net/wiki/x/AgCUgQ"
          target="_blank"
          rel="noopener noreferrer"
          >Zum Hilfeartikel.</a
        >
      </v-alert>
    </v-row>
    <v-row no-gutters align="center">
      <v-switch
        label="Verstecken, falls leer"
        v-model="hide_when_empty"
      ></v-switch>
      <v-tooltip color="white" top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            color="primary"
            style="margin-left: 8px; cursor: help"
            small
            v-bind="attrs"
            v-on="on"
            >{{ mdiHelpCircle }}</v-icon
          >
        </template>
        <span :style="{ color: textColor }"
          >Falls das Feld in einem Profil nicht ausgefüllt sein sollte, kann
          dieses in der Profilansicht versteckt werden. Dies ist sinnvoll, falls
          das Feld bei den meisten Profilen leer sein wird.</span
        >
      </v-tooltip>
    </v-row>
    <v-row no-gutters>
      <ContentfulSelect
        label="Wer darf lesen?"
        required
        :options="READ_ACCESS_SELECTION_ITEMS"
        :value="selectedReadAccessSelectionItem.value"
        @input="handleReadAccessSelection"
      />
    </v-row>
    <v-row no-gutters>
      <ContentfulSelect
        label="Wer darf schreiben?"
        required
        :options="WRITE_ACCESS_SELECTION_ITEMS"
        :value="selectedWriteAccessSelectionItem.value"
        @input="handleWriteAccessSelection"
      />
    </v-row>
    <v-row class="d-none" no-gutters justify="end" style="flex-wrap: nowrap">
      <TextButton
        @click="cancel"
        style="margin-right: 8px"
        label="Abbrechen"
      ></TextButton>
      <Button :disabled="!enableSave" @click="save">Speichern</Button>
    </v-row>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, Ref, ref, watch } from "vue"
import InputField from "app/ui-primitives/forms/InputField.vue"
import Select from "app/ui-primitives/forms/Select.vue"
import CustomAttributesSelectValues from "app/modules/custom-attributes/CustomAttributesSelectValues.vue"
import Button from "components/UI/Button.vue"
import TextButton from "components/UI/TextButton.vue"
import {
  CustomAttributeDefinition,
  CustomAttributeReadAccessEnum,
  CustomAttributeWriteAccessEnum,
  CustomAttributeTypeEnum,
} from "suus-api"
import ErrorText from "app/ui-primitives/text/ErrorText.vue"
import { mdiHelpCircle } from "@mdi/js"
import { ContentfulOption } from "app/ui-primitives/forms/ContentfulSelect.vue"
import ContentfulSelect from "app/ui-primitives/forms/ContentfulSelect.vue"
import ChipSelectCombo from "app/ui-primitives/forms/ChipSelectCombo.vue"
import CustomAttributeTypeSelection from "app/modules/custom-attributes/form/CustomAttributeTypeSelection.vue"
import {
  CUSTOM_ATTRIBUTE_READ_ACCESS_ICONS,
  CUSTOM_ATTRIBUTE_WRITE_ACCESS_ICONS,
} from "app/modules/custom-attributes/types"
import { useVuetifyTheme } from "features/common/vuetify/useVuetify"

type ReadAccessSelectionItem = Omit<ContentfulOption, "value"> & {
  value: CustomAttributeReadAccessEnum
}
const READ_ACCESS_SELECTION_ITEMS: ReadAccessSelectionItem[] = [
  {
    text: "Admins",
    value: "readable_by_admins",
    icon: CUSTOM_ATTRIBUTE_READ_ACCESS_ICONS.readable_by_admins.icon,
    description: "<b>Admins</b> können dieses Feld ansehen",
  },
  {
    text: "Adressverwalter",
    value: "readable_by_managers",
    icon: CUSTOM_ATTRIBUTE_READ_ACCESS_ICONS.readable_by_managers.icon,
    description:
      "<b>Adressverwalter</b> & <b>Admins</b> können dieses Feld ansehen",
  },
  {
    text: "Alle Bearbeiter",
    value: "readable_by_editors",
    icon: CUSTOM_ATTRIBUTE_READ_ACCESS_ICONS.readable_by_editors.icon,
    description:
      "Alle Bearbeiter (<b>Admins</b>, <b>Adressverwalter</b> & <b>Profilbesitzer</b>) können dieses Feld ansehen",
  },
  {
    text: "Alle",
    value: "readable_by_viewers",
    icon: CUSTOM_ATTRIBUTE_READ_ACCESS_ICONS.readable_by_viewers.icon,
    description: "<b>Alle Nutzer</b> können dieses Feld ansehen",
  },
]

type WriteAccessSelectionItem = Omit<ContentfulOption, "value"> & {
  value: CustomAttributeWriteAccessEnum
}

const WRITE_ACCESS_SELECTION_ITEMS: WriteAccessSelectionItem[] = [
  {
    text: "Admins",
    value: "writeable_by_admins",
    icon: CUSTOM_ATTRIBUTE_WRITE_ACCESS_ICONS.writeable_by_admins.icon,
    description: "<b>Admins</b> können dieses Feld bearbeiten",
  },
  {
    text: "Adressverwalter",
    value: "writeable_by_managers",
    icon: CUSTOM_ATTRIBUTE_WRITE_ACCESS_ICONS.writeable_by_managers.icon,
    description:
      "<b>Adressverwalter</b> & <b>Admins</b> können dieses Feld bearbeiten",
  },
  {
    text: "Alle Bearbeiter",
    value: "writeable_by_editors",
    icon: CUSTOM_ATTRIBUTE_WRITE_ACCESS_ICONS.writeable_by_editors.icon,
    description:
      "Alle Bearbeiter (<b>Admins</b>, <b>Adressverwalter</b> & <b>Profilbesitzer</b>) können dieses Feld bearbeiten",
  },
]

/**
 * API:
 * @function submitForm - triggers the submit function & validation
 * @function resetForm - triggers the reset function
 * @emits commit - when save was triggered and the form is valid and should be committed
 * @emits dirty - when the form is dirty (or create mode)
 */

export default defineComponent({
  name: "CustomAttributesForm",
  components: {
    CustomAttributeTypeSelection,
    ChipSelectCombo,
    ContentfulSelect,
    ErrorText,
    TextButton,
    Button,
    CustomAttributesSelectValues,
    Select,
    InputField,
  },
  props: {
    initialValue: {
      type: Object as PropType<CustomAttributeDefinition>,
      hide_when_empty: true,
    },
  },
  setup(props, { emit }) {
    const formRef = ref<HTMLFormElement | null>(null)
    const isCreate = computed(() => props.initialValue == undefined)

    const attributeName = ref(props.initialValue?.name ?? "")
    const attributeDescription = ref(props.initialValue?.description ?? "")
    const attributeType: Ref<CustomAttributeTypeEnum> = ref(
      props.initialValue?.attribute_type ?? "String"
    )
    const selectionValues = ref(props.initialValue?.config ?? [])
    const hide_when_empty = ref(props.initialValue?.hide_when_empty ?? false)
    const readAccess: Ref<CustomAttributeReadAccessEnum> = ref(
      props.initialValue?.read_access ?? "readable_by_admins"
    )
    const writeAccess: Ref<CustomAttributeWriteAccessEnum> = ref(
      props.initialValue?.write_access ?? "writeable_by_admins"
    )

    watch(
      () => props.initialValue,
      (newVal) => {
        attributeName.value = newVal?.name ?? ""
        attributeDescription.value = newVal?.description ?? ""
        attributeType.value = newVal?.attribute_type ?? "String"
        selectionValues.value = newVal?.config ?? []
        hide_when_empty.value = newVal?.hide_when_empty ?? false
        readAccess.value = newVal?.read_access ?? "readable_by_admins"
        writeAccess.value = newVal?.write_access ?? "writeable_by_admins"
      },
      { deep: true }
    )

    const handleTypeSelection = (selection: CustomAttributeTypeEnum) => {
      attributeType.value = selection
      if (attributeType.value !== "Select") {
        selectionValues.value = []
      }
      revalidateSelection()
    }

    const handleSelectTypeValuesChange = (values: string[]) => {
      selectionValues.value = values
      revalidateSelection()
    }

    const isCurrentlySelectType = computed(() => {
      return attributeType.value === "Select"
    })

    const handleReadAccessSelection = (
      selection: CustomAttributeReadAccessEnum
    ) => {
      readAccess.value = selection
    }
    const selectedReadAccessSelectionItem = computed(() => {
      return READ_ACCESS_SELECTION_ITEMS.find(
        (item) => item.value === readAccess.value
      )
    })

    const handleWriteAccessSelection = (
      selection: CustomAttributeWriteAccessEnum
    ) => {
      writeAccess.value = selection
    }

    const selectedWriteAccessSelectionItem = computed(() => {
      return WRITE_ACCESS_SELECTION_ITEMS.find(
        (item) => item.value === writeAccess.value
      )
    })

    const save = () => {
      const _valid = validate()
      if (!_valid) {
        return
      }
      const customAttributeDefinition: CustomAttributeDefinition = {
        ...props.initialValue,
        name: attributeName.value,
        description: attributeDescription.value,
        attribute_type: attributeType.value,
        config: selectionValues.value,
        hide_when_empty: hide_when_empty.value,
        attributable_type:
          props.initialValue?.attributable_type ?? "PersonalRecord",
        read_access: readAccess.value,
        write_access: writeAccess.value,
      }
      emit("commit", customAttributeDefinition)
    }

    const reset = () => {
      const initialVal = props.initialValue
      attributeName.value = initialVal?.name ?? ""
      attributeDescription.value = initialVal?.description ?? ""
      attributeType.value = initialVal?.attribute_type ?? "String"
      selectionValues.value = initialVal?.config ?? []
      hide_when_empty.value = initialVal?.hide_when_empty ?? false
      readAccess.value = initialVal?.read_access ?? "readable_by_admins"
      writeAccess.value = initialVal?.write_access ?? "writeable_by_admins"
    }

    const attributeTypeChanged = computed(() => {
      return (
        attributeType.value !== props.initialValue?.attribute_type &&
        !isCreate.value
      )
    })

    const dirty = computed(() => {
      const areSelectionTypesEqual = (
        selectionValues: string[],
        initialSelectionValues: string[]
      ) => {
        if (selectionValues.length !== initialSelectionValues.length) {
          return false
        }
        for (let i = 0; i < selectionValues.length; i++) {
          if (selectionValues[i] !== initialSelectionValues[i]) {
            return false
          }
        }
        return true
      }
      return (
        attributeName.value !== (props.initialValue?.name ?? "") ||
        attributeDescription.value !==
          (props.initialValue?.description ?? "") ||
        attributeType.value !==
          (props.initialValue?.attribute_type ?? "String") ||
        !areSelectionTypesEqual(
          selectionValues.value,
          props.initialValue?.config ?? []
        ) ||
        hide_when_empty.value !==
          (props.initialValue?.hide_when_empty ?? false) ||
        readAccess.value !==
          (props.initialValue?.read_access ?? "readable_by_admins") ||
        writeAccess.value !==
          (props.initialValue?.write_access ?? "writeable_by_admins")
      )
    })

    const enableSave = computed(() => {
      return dirty.value || isCreate.value
    })

    watch(
      () => enableSave.value,
      (newVal) => {
        emit("dirty", newVal)
      },
      { immediate: true }
    )

    type ValidationErrors = {
      selectionValues?: string
    }
    const validationErrors: Ref<ValidationErrors> = ref({})
    const validateSelection = () => {
      if (isCurrentlySelectType.value && selectionValues.value.length === 0) {
        return "Bitte fügen Sie mindestens einen Wert hinzu"
      } else {
        return undefined
      }
    }
    const revalidateSelection = () => {
      const selectionValuesError = validateSelection()
      if (selectionValuesError) {
        validationErrors.value = {
          ...validationErrors.value,
          selectionValues: selectionValuesError,
        }
      } else {
        delete validationErrors.value.selectionValues
      }
    }
    const validate = () => {
      const isValid = formRef.value.validate()
      validationErrors.value = {}
      const errors: ValidationErrors = {}
      const selectionValuesError = validateSelection()
      if (selectionValuesError) {
        errors.selectionValues = selectionValuesError
      }
      validationErrors.value = errors
      return Object.keys(errors).length === 0 && isValid
    }

    const valid = ref(false)

    const textFieldRules = [
      (v: string) => !!v || "Dieses Feld darf nicht leer sein",
    ]

    const submitForm = () => {
      save()
    }

    const resetForm = () => {
      reset()
    }

    const vuetifyTheme = useVuetifyTheme()
    const textColor = computed(() => vuetifyTheme.textColor)

    return {
      attributeName,
      attributeDescription,
      attributeType,
      selectionValues,
      hide_when_empty,
      readAccess,
      writeAccess,
      handleTypeSelection,
      isCurrentlySelectType,
      READ_ACCESS_SELECTION_ITEMS,
      WRITE_ACCESS_SELECTION_ITEMS,
      handleReadAccessSelection,
      selectedReadAccessSelectionItem,
      handleWriteAccessSelection,
      selectedWriteAccessSelectionItem,
      save,
      cancel: reset,
      attributeTypeChanged,
      dirty,
      enableSave,
      validationErrors,
      valid,
      textFieldRules,
      formRef,
      handleSelectTypeValuesChange,
      mdiHelpCircle,
      submitForm,
      resetForm,
      textColor,
    }
  },
})
</script>

<style>
.v-tooltip__content {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
}
</style>
